import React, { useState, useEffect, useCallback } from 'react';
import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { coffeeShopReviewPaths } from '../../coffee_card_web/api/paths';
import { getAuthToken } from '../../../utils/authTokens';
import { Button, Input } from '../../../ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faTimes,
    faBolt,
    faStore,
    faSpinner,
    faSearch,
    faPencilAlt,
    faExternalLinkAlt,
    faLink
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { osmViewUrl } from '../../coffee_shops/utils/mapLinks';

interface PendingShop {
    _id: string;
    name: string;
    address?: string;
    city?: string;
    country?: string;
    coordinates?: { lat: number; lng: number };
    place_id?: string;
    source?: string;
    confidence?: 'high' | 'medium' | 'low';
    submittedBy?: { username?: string; userEmoji?: string };
}

interface GoogleCandidate {
    place_id: string;
    name: string;
    geometry: { location: { lat: number; lng: number } };
    vicinity?: string;
    formatted_address?: string;
    rating?: number;
    price_level?: number;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

function mapsUrl(lat: number, lng: number): string {
    return osmViewUrl(lat, lng);
}

const ShopReviewQueue: React.FC = () => {
    const [shops, setShops] = useState<PendingShop[]>([]);
    const [stats, setStats] = useState<{ pending: number; highConfidence: number } | null>(null);
    const [loading, setLoading] = useState(true);
    const [actingId, setActingId] = useState<string | null>(null);
    const [editShopId, setEditShopId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{ name: string; address: string; lat: string; lng: string; city: string; country: string }>({
        name: '',
        address: '',
        lat: '',
        lng: '',
        city: '',
        country: ''
    });
    const [searchShopId, setSearchShopId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [candidates, setCandidates] = useState<GoogleCandidate[]>([]);
    const [linkLoadingId, setLinkLoadingId] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [pendingRes, statsRes] = await Promise.all([
                apiClient.get(`${url}${coffeeShopReviewPaths.pending()}`, authHeaders()),
                apiClient.get(`${url}${coffeeShopReviewPaths.stats()}`, authHeaders())
            ]);
            setShops(pendingRes.data?.shops || []);
            setStats(statsRes.data?.stats || null);
        } catch (err) {
            toast.error('Failed to load review queue');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const approve = async (id: string) => {
        setActingId(id);
        try {
            await apiClient.put(`${url}${coffeeShopReviewPaths.approve(id)}`, {}, authHeaders());
            toast.success('Shop approved');
            fetchData();
        } catch {
            toast.error('Failed to approve');
        } finally {
            setActingId(null);
        }
    };

    const quickApprove = async (id: string) => {
        setActingId(id);
        try {
            await apiClient.put(`${url}${coffeeShopReviewPaths.quickApprove(id)}`, {}, authHeaders());
            toast.success('Shop approved');
            fetchData();
        } catch {
            toast.error('Failed to approve');
        } finally {
            setActingId(null);
        }
    };

    const reject = async (id: string) => {
        setActingId(id);
        try {
            await apiClient.put(`${url}${coffeeShopReviewPaths.reject(id)}`, {}, authHeaders());
            toast.success('Shop rejected');
            fetchData();
        } catch {
            toast.error('Failed to reject');
        } finally {
            setActingId(null);
        }
    };

    const runSearchGoogle = async (shop: PendingShop, query?: string) => {
        setSearchShopId(shop._id);
        setSearchLoading(true);
        setCandidates([]);
        const q = (query ?? (searchQuery || shop.name)).trim();
        setSearchQuery(q);
        try {
            const res = await apiClient.put(
                `${url}${coffeeShopReviewPaths.searchGoogle(shop._id)}`,
                { query: q || shop.name },
                authHeaders()
            );
            setCandidates(res.data?.candidates || []);
        } catch {
            toast.error('Search failed');
            setCandidates([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const linkPlace = async (shopId: string, c: GoogleCandidate) => {
        setLinkLoadingId(c.place_id);
        try {
            await apiClient.put(
                `${url}${coffeeShopReviewPaths.linkPlace(shopId)}`,
                {
                    placeId: c.place_id,
                    name: c.name,
                    address: c.formatted_address || c.vicinity,
                    coordinates: {
                        lat: c.geometry.location.lat,
                        lng: c.geometry.location.lng
                    },
                    rating: c.rating,
                    priceLevel: c.price_level
                },
                authHeaders()
            );
            toast.success('Shop linked to Google place');
            setSearchShopId(null);
            setCandidates([]);
            fetchData();
        } catch {
            toast.error('Failed to link place');
        } finally {
            setLinkLoadingId(null);
        }
    };

    const startEdit = (shop: PendingShop) => {
        setEditShopId(shop._id);
        setEditForm({
            name: shop.name,
            address: shop.address || '',
            lat: shop.coordinates?.lat != null ? String(shop.coordinates.lat) : '',
            lng: shop.coordinates?.lng != null ? String(shop.coordinates.lng) : '',
            city: shop.city || '',
            country: shop.country || ''
        });
    };

    const saveEdit = async (id: string) => {
        setActingId(id);
        try {
            const lat = editForm.lat.trim() ? parseFloat(editForm.lat) : undefined;
            const lng = editForm.lng.trim() ? parseFloat(editForm.lng) : undefined;
            await apiClient.put(
                `${url}${coffeeShopReviewPaths.update(id)}`,
                {
                    name: editForm.name.trim() || undefined,
                    address: editForm.address || undefined,
                    coordinates: lat != null && lng != null && !isNaN(lat) && !isNaN(lng) ? { lat, lng } : undefined,
                    city: editForm.city.trim() || undefined,
                    country: editForm.country.trim() || undefined
                },
                authHeaders()
            );
            toast.success('Shop details updated');
            setEditShopId(null);
            fetchData();
        } catch {
            toast.error('Failed to update shop');
        } finally {
            setActingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {stats && (
                <div className="flex gap-4 text-sm">
                    <span>{stats.pending} pending</span>
                    <span>{stats.highConfidence} high confidence</span>
                </div>
            )}
            {shops.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No shops pending review</p>
            ) : (
                <div className="space-y-2">
                    {shops.map((shop) => {
                        const isActing = actingId === shop._id;
                        const isHigh = shop.confidence === 'high';
                        const isEditing = editShopId === shop._id;
                        const isSearchingThis = searchShopId === shop._id;

                        return (
                            <div
                                key={shop._id}
                                className="p-3 border border-ink/15 rounded-lg bg-paper space-y-3"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium flex items-center gap-2">
                                            <FontAwesomeIcon icon={faStore} className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                            {shop.name}
                                        </div>
                                        {shop.address && (
                                            <div className="text-xs text-muted-foreground mt-1">{shop.address}</div>
                                        )}
                                        {shop.coordinates?.lat != null && shop.coordinates?.lng != null && (
                                            <a
                                                href={mapsUrl(shop.coordinates.lat, shop.coordinates.lng)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-sky hover:underline mt-1 inline-flex items-center gap-1"
                                            >
                                                {shop.coordinates.lat.toFixed(4)}, {shop.coordinates.lng.toFixed(4)}
                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="h-2.5 w-2.5" />
                                            </a>
                                        )}
                                        {shop.submittedBy && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                by {shop.submittedBy.userEmoji || ''} {shop.submittedBy.username}
                                            </div>
                                        )}
                                    </div>
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                                            isHigh
                                                ? 'bg-green-100 text-green-800'
                                                : shop.confidence === 'medium'
                                                ? 'bg-amber-100 text-amber-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}
                                    >
                                        {shop.confidence || 'low'}
                                    </span>
                                </div>

                                {/* Investigation row */}
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => runSearchGoogle(shop)}
                                        disabled={searchLoading}
                                    >
                                        {isSearchingThis && searchLoading ? (
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                                        ) : (
                                            <FontAwesomeIcon icon={faSearch} className="mr-1" />
                                        )}
                                        Search Google
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => (isEditing ? setEditShopId(null) : startEdit(shop))}
                                        disabled={isActing}
                                    >
                                        <FontAwesomeIcon icon={faPencilAlt} className="mr-1" />
                                        {isEditing ? 'Cancel edit' : 'Edit details'}
                                    </Button>
                                </div>

                                {/* Google candidates (after search) */}
                                {isSearchingThis && (
                                    <div className="border rounded-lg p-2 bg-muted/20 space-y-2">
                                        {searchLoading ? (
                                            <p className="text-sm text-muted-foreground">Searching...</p>
                                        ) : candidates.length === 0 ? (
                                            <p className="text-sm text-muted-foreground">No results (2 km radius)</p>
                                        ) : (
                                            <>
                                                <p className="text-sm font-medium">Select a place to link</p>
                                                <div className="max-h-40 overflow-y-auto space-y-1">
                                                    {candidates.map((c) => (
                                                        <div
                                                            key={c.place_id}
                                                            className="flex items-center justify-between gap-2 p-2 rounded bg-paper border text-sm"
                                                        >
                                                            <div className="min-w-0">
                                                                <div className="font-medium truncate">{c.name}</div>
                                                                <div className="text-xs text-muted-foreground truncate">
                                                                    {c.formatted_address || c.vicinity}
                                                                </div>
                                                                {c.rating != null && (
                                                                    <span className="text-xs text-gold">★ {c.rating}</span>
                                                                )}
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => linkPlace(shop._id, c)}
                                                                disabled={linkLoadingId !== null}
                                                            >
                                                                {linkLoadingId === c.place_id ? (
                                                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                                                ) : (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faLink} className="mr-1" />
                                                                        Link
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Edit form */}
                                {isEditing && (
                                    <div className="border rounded-lg p-3 space-y-2 bg-muted/10">
                                        <div>
                                            <label className="text-xs font-medium block mb-1">Name</label>
                                            <Input
                                                value={editForm.name}
                                                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                                placeholder="Shop name"
                                                className="text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium block mb-1">Address</label>
                                            <Input
                                                value={editForm.address}
                                                onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                                                placeholder="Address"
                                                className="text-sm"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs font-medium block mb-1">Lat</label>
                                                <Input
                                                    value={editForm.lat}
                                                    onChange={(e) => setEditForm((f) => ({ ...f, lat: e.target.value }))}
                                                    placeholder="Latitude"
                                                    className="text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium block mb-1">Lng</label>
                                                <Input
                                                    value={editForm.lng}
                                                    onChange={(e) => setEditForm((f) => ({ ...f, lng: e.target.value }))}
                                                    placeholder="Longitude"
                                                    className="text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs font-medium block mb-1">City</label>
                                                <Input
                                                    value={editForm.city}
                                                    onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))}
                                                    placeholder="City"
                                                    className="text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium block mb-1">Country</label>
                                                <Input
                                                    value={editForm.country}
                                                    onChange={(e) => setEditForm((f) => ({ ...f, country: e.target.value }))}
                                                    placeholder="Country"
                                                    className="text-sm"
                                                />
                                            </div>
                                        </div>
                                        <Button size="sm" onClick={() => saveEdit(shop._id)} disabled={isActing}>
                                            Save
                                        </Button>
                                    </div>
                                )}

                                {/* Decision row */}
                                <div className="flex flex-wrap gap-2 pt-1 border-t border-ink/10">
                                    {isHigh && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => quickApprove(shop._id)}
                                            disabled={isActing}
                                        >
                                            <FontAwesomeIcon icon={faBolt} className="mr-1" />
                                            Quick approve
                                        </Button>
                                    )}
                                    <Button size="sm" onClick={() => approve(shop._id)} disabled={isActing}>
                                        <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                        Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => reject(shop._id)}
                                        disabled={isActing}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                        Reject
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ShopReviewQueue;
